import React, { useState } from 'react';
import {
  Modal,
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Divider,
  Typography,
  App,
  Input,
  Form,
} from 'antd';
import {
  FilePdfOutlined,
  FileZipOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
  MailOutlined,
  SyncOutlined,
  UserSwitchOutlined,
  DownloadOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import {
  useGetFacturaDetalleQuery,
  useTimbrarFacturaMutation,
  useCancelarFacturaMutation,
  useSincronizarCancelacionMutation,
  useReexpedirGlobalMutation,
  useLazyGetTicketsDeFacturaQuery,
  useEnviarFacturaEmailMutation,
  useGetFormasPagoQuery,
  useGetMetodosPagoQuery,
  useGetUsosCFDIQuery,
} from '../../services/facturacionApi';
import { useGetOrderQuery } from '../../services/ordersApi';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { getApiBaseUrl } from '../../config/api';
import { getErrorMessage } from '../../utils/errorHelpers';
import { useAppSelector } from '../../hooks/redux';
import { canManageFacturas, canOperateFacturas, isSupportUser } from '../../utils/roleHelpers';
import { estadoVisibleFactura, ETIQUETA_ESTADO_FACTURA } from '../../utils/facturaEstado';
import { exportTablaCSV } from '../../utils/reportExport';
import SolicitarFacturaModal from './SolicitarFacturaModal';
import CrearFacturaModal from './CrearFacturaModal';

const { Text } = Typography;

interface FacturaDetalleModalProps {
  facturaId: number;
  visible: boolean;
  onClose: () => void;
  /** Callback opcional cuando la factura es regenerada, pasa el nuevo facturaId */
  onFacturaRegenerated?: (newFacturaId: number) => void;
  /** Callback opcional para refrescar datos después de timbrar o cancelar */
  onRefresh?: () => void;
  /**
   * La orden desde la que se abrió la factura. En una global la factura ampara muchas
   * órdenes; "Facturar a nombre del cliente" actúa sobre ESTA.
   */
  contextOrderId?: string;
}

const FacturaDetalleModal: React.FC<FacturaDetalleModalProps> = ({
  facturaId,
  visible,
  onClose,
  onFacturaRegenerated,
  onRefresh,
  contextOrderId,
}) => {
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { message, modal } = App.useApp();
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showRegenerateWithoutOrderModal, setShowRegenerateWithoutOrderModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showFacturarTicketModal, setShowFacturarTicketModal] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const esSoporte = isSupportUser(user?.email);
  // Esconder no es la barrera (el backend exige el rol); evita toparse con un 403.
  const puedeGestionar = canManageFacturas(user?.role, esSoporte);
  const puedeOperar = canOperateFacturas(user?.role, esSoporte);
  const [emailForm] = Form.useForm();

  const { data: factura, isLoading, refetch } = useGetFacturaDetalleQuery(facturaId, {
    skip: !visible,
  });
  const [timbrarFactura, { isLoading: timbrandoFactura }] = useTimbrarFacturaMutation();
  const [cancelarFactura, { isLoading: cancelandoFactura }] = useCancelarFacturaMutation();
  const [sincronizarCancelacion, { isLoading: consultandoSat }] = useSincronizarCancelacionMutation();
  const [reexpedirGlobal, { isLoading: reexpidiendo }] = useReexpedirGlobalMutation();
  const [cargarTickets, { isFetching: cargandoTickets }] = useLazyGetTicketsDeFacturaQuery();
  const [enviarFacturaEmail, { isLoading: enviandoEmail }] = useEnviarFacturaEmailMutation();

  // Obtener la orden relacionada si existe
  // La de contexto si se abrió desde una orden: en una global, factura.orderId es solo una
  // de las muchas que ampara.
  const orderIdRelacionada = contextOrderId || factura?.orderId;
  const { data: order } = useGetOrderQuery(orderIdRelacionada || '', {
    skip: !visible || !orderIdRelacionada,
  });

  const estadoVisible = factura ? estadoVisibleFactura(factura.estado, factura.estatusCancelacion) : 'Borrador';
  const amparaVarias = (factura?.ordenesCubiertas ?? 1) > 1;

  // Catálogos para mostrar descripciones
  const { data: formasPago } = useGetFormasPagoQuery(undefined, { skip: !visible });
  const { data: metodosPago } = useGetMetodosPagoQuery(undefined, { skip: !visible });
  const { data: usosCFDI } = useGetUsosCFDIQuery(undefined, { skip: !visible });

  // Helper para obtener descripción de catálogo
  const getDescripcion = (
    catalogo: Array<{ clave: string; descripcion: string }> | undefined,
    clave: string
  ): string => {
    if (!catalogo || !clave) return clave;
    // Buscar coincidencia exacta
    let item = catalogo.find(c => c.clave === clave);
    // Si no, buscar sin cero inicial
    if (!item) {
      const sinCero = clave.replace(/^0/, '');
      item = catalogo.find(c => c.clave === sinCero);
    }
    // Si no, buscar con cero inicial
    if (!item && clave.length === 1 && /^\d$/.test(clave)) {
      const conCero = '0' + clave;
      item = catalogo.find(c => c.clave === conCero);
    }
    return item ? `${clave} - ${item.descripcion}` : clave;
  };

  const handleDownloadPDF = async () => {
    if (!factura) return;

    try {
      const token = localStorage.getItem('accessToken');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}${factura.pdfUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al descargar PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${factura.serie}-${factura.folio}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      message.error('Error al descargar el PDF');
    }
  };

  const handleDownloadXML = async () => {
    if (!factura || !factura.xmlUrl) return;

    try {
      const token = localStorage.getItem('accessToken');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}${factura.xmlUrl}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al descargar XML: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura-${factura.serie}-${factura.folio}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading XML:', err);
      message.error('Error al descargar el XML');
    }
  };

  const handleTimbrar = async () => {
    if (!factura) return;

    try {
      await timbrarFactura(factura.facturaId).unwrap();
      message.success('Factura timbrada exitosamente');
      refetch();
      // Refrescar datos de la orden en el componente padre
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: unknown) {
      console.error('Error al timbrar factura:', error);
      const errorMsg = getErrorMessage(error, 'Error al timbrar la factura');
      message.error(errorMsg, 8);
    }
  };

  const handleCancelar = () => {
    if (!factura) return;

    modal.confirm({
      title: '¿Cancelar factura?',
      content: '¿Está seguro de que desea cancelar esta factura? Esta acción es irreversible.',
      okText: 'Sí, cancelar',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const resultado = await cancelarFactura({
            facturaId: factura.facturaId,
            data: { motivo: '02' },
          }).unwrap();
          // "Solicitada" no es "cancelada": si el receptor debe aceptarla, el SAT tarda.
          if (resultado.confirmada) message.success(resultado.mensaje);
          else message.info(resultado.mensaje, 8);
          refetch();
          // Refrescar datos de la orden en el componente padre
          if (onRefresh) {
            onRefresh();
          }
        } catch (error: unknown) {
          console.error('Error al cancelar factura:', error);
          const errorMsg = getErrorMessage(error, 'Error al cancelar la factura');
          message.error(errorMsg, 8);
        }
      },
    });
  };

  const handleConsultarSat = async () => {
    if (!factura) return;
    try {
      const resultado = await sincronizarCancelacion(factura.facturaId).unwrap();
      if (resultado.confirmada) message.success(resultado.mensaje);
      else if (resultado.rechazada) message.warning(resultado.mensaje, 8);
      else message.info(resultado.mensaje, 8);
      refetch();
      onRefresh?.();
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'No se pudo consultar al SAT'), 8);
    }
  };

  const handleReexpedirGlobal = () => {
    if (!factura) return;
    modal.confirm({
      title: '¿Reexpedir la factura global ahora?',
      content: `Se emitirá una global nueva sin los ${factura.ticketsPorSacar ?? 0} ticket(s) que sus clientes ya facturaron, y la actual se cancelará con motivo 04. Si no lo haces, ocurre sola de madrugada.`,
      okText: 'Reexpedir',
      cancelText: 'No',
      onOk: async () => {
        try {
          const resultado = await reexpedirGlobal(factura.facturaId).unwrap();
          if (resultado.completada) message.success(resultado.mensaje, 8);
          else message.warning(resultado.mensaje, 10);
          refetch();
          onRefresh?.();
        } catch (error: unknown) {
          message.error(getErrorMessage(error, 'No se pudo reexpedir la factura global'), 8);
        }
      },
    });
  };

  // Para cuadrar la global contra el reporte de la terminal: ticket, fecha, monto y referencia.
  const handleExportarTickets = async () => {
    if (!factura) return;
    try {
      const tickets = await cargarTickets(factura.facturaId).unwrap();
      exportTablaCSV(
        ['Ticket', 'Fecha', 'Total', 'Forma de pago', 'Ref. terminal'],
        tickets.map((t) => [
          t.orderNumber,
          formatInTimeZone(t.fecha, 'DD/MM/YYYY HH:mm'),
          t.total.toFixed(2),
          t.formaPago ?? '',
          t.referenciaTerminal ?? '',
        ]),
        `tickets-${factura.serie}-${factura.folio}`,
      );
    } catch (error: unknown) {
      message.error(getErrorMessage(error, 'No se pudieron obtener los tickets de la factura'), 8);
    }
  };

  const handleCopyUUID = () => {
    if (factura?.uuid) {
      navigator.clipboard.writeText(factura.uuid);
      message.success('UUID copiado al portapapeles');
    }
  };

  const handleOpenEmailModal = () => {
    emailForm.setFieldsValue({
      emailDestino: factura?.clienteEmail || '',
      mensaje: '',
    });
    setShowEmailModal(true);
  };

  const handleEnviarEmail = async (values: { emailDestino?: string; mensaje?: string }) => {
    if (!factura) return;

    try {
      const result = await enviarFacturaEmail({
        facturaId: factura.facturaId,
        request: {
          emailDestino: values.emailDestino || undefined,
          mensaje: values.mensaje || undefined,
        },
      }).unwrap();

      if (result.success) {
        message.success(`Factura enviada exitosamente a ${result.email}`);
        setShowEmailModal(false);
        emailForm.resetFields();
      } else {
        message.error(result.error || 'Error al enviar la factura');
      }
    } catch (error: unknown) {
      console.error('Error al enviar factura por email:', error);
      const errorMsg = getErrorMessage(error, 'Error al enviar la factura por email');
      message.error(errorMsg, 8);
    }
  };

  const getEstadoTag = () => {
    const { texto, color } = ETIQUETA_ESTADO_FACTURA[estadoVisible];
    return (
      <>
        <Tag color={color}>{texto}</Tag>
        {factura?.esGlobal && <Tag color="blue">Global · {factura.ordenesCubiertas ?? 0} órdenes</Tag>}
        {(factura?.ticketsPorSacar ?? 0) > 0 && <Tag color="gold">{factura!.ticketsPorSacar} ticket(s) por sacar</Tag>}
      </>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <span>Detalle de Factura</span>
          {factura && getEstadoTag()}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={isMobile ? '100%' : 900}
      footer={[
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
    >
      {isLoading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {!isLoading && factura && (
        <>
          {/* Información General */}
          <Descriptions title="Información General" bordered column={isMobile ? 1 : 2} size="small">
            <Descriptions.Item label="Serie">{factura.serie}</Descriptions.Item>
            <Descriptions.Item label="Folio">{factura.folio}</Descriptions.Item>
            <Descriptions.Item label="Fecha">
              {formatInTimeZone(factura.fecha, 'DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">{getEstadoTag()}</Descriptions.Item>
            <Descriptions.Item label="Forma de Pago">{getDescripcion(formasPago, factura.formaPago)}</Descriptions.Item>
            <Descriptions.Item label="Método de Pago">{getDescripcion(metodosPago, factura.metodoPago)}</Descriptions.Item>
            <Descriptions.Item label="Uso de CFDI" span={2}>
              {getDescripcion(usosCFDI, factura.usoCFDI)}
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          {/* Información del Cliente */}
          <Descriptions title="Cliente" bordered column={isMobile ? 1 : 2} size="small">
            <Descriptions.Item label="RFC">{factura.clienteRFC}</Descriptions.Item>
            <Descriptions.Item label="Razón Social" span={isMobile ? 1 : 2}>
              {factura.clienteRazonSocial}
            </Descriptions.Item>
            {factura.clienteEmail && (
              <Descriptions.Item label="Email" span={2}>
                {factura.clienteEmail}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Divider />

          {/* Importes */}
          <Descriptions title="Importes" bordered column={isMobile ? 1 : 2} size="small">
            <Descriptions.Item label="Subtotal">
              ${factura.subtotal.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Impuestos">
              ${factura.totalImpuestos.toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label="Total" span={2}>
              <Text strong style={{ fontSize: 16, color: '#52c41a' }}>
                ${factura.total.toFixed(2)}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {/* Información de Timbrado */}
          {factura.uuid && (
            <>
              <Divider />
              <Descriptions title="Timbrado Fiscal" bordered column={1} size="small">
                <Descriptions.Item label="UUID">
                  <Space>
                    <Text copyable={{ text: factura.uuid }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {factura.uuid}
                    </Text>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={handleCopyUUID}
                    />
                  </Space>
                </Descriptions.Item>
                {factura.fechaTimbrado && (
                  <Descriptions.Item label="Fecha de Timbrado">
                    {formatInTimeZone(factura.fechaTimbrado, 'DD/MM/YYYY HH:mm:ss')}
                  </Descriptions.Item>
                )}
                {factura.rfcProvCertif && (
                  <Descriptions.Item label="PAC Certificador">
                    {factura.rfcProvCertif}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}

          {/* Información de la Orden */}
          {factura.orderId && (
            <>
              <Divider />
              <Descriptions title="Orden Relacionada" bordered column={isMobile ? 1 : 2} size="small">
                <Descriptions.Item label="ID de Orden">
                  {factura.orderId.substring(0, 8)}...
                </Descriptions.Item>
                {factura.orderNumber && (
                  <Descriptions.Item label="Número de Orden">
                    {factura.orderNumber}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </>
          )}

          <Divider />

          {/* Botones de Acción */}
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            style={{ width: '100%' }}
            wrap={!isMobile}
          >
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPDF}
              size="large"
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              Descargar PDF
            </Button>

            {factura.xmlUrl && (
              <Button
                icon={<FileZipOutlined />}
                onClick={handleDownloadXML}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Descargar XML
              </Button>
            )}

            {factura.uuid && factura.estado === 'Timbrada' && (
              <Button
                icon={<MailOutlined />}
                onClick={handleOpenEmailModal}
                loading={enviandoEmail}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Enviar por Email
              </Button>
            )}

            {(amparaVarias || factura.esGlobal ? puedeGestionar : puedeOperar) && !factura.uuid && factura.estado !== 'Cancelada' && (
              <Button
                type="primary"
                icon={<SafetyCertificateOutlined />}
                onClick={handleTimbrar}
                loading={timbrandoFactura}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto', backgroundColor: '#1890ff' }}
              >
                Timbrar Factura
              </Button>
            )}

            {/* Botón de Regenerar - para facturas en borrador (CON o SIN orden). Una global
                nunca se regenera desde una orden: se llevaba la global completa. */}
            {puedeOperar && factura.estado === 'Borrador' && !amparaVarias && (
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (factura.orderId) {
                    // Factura CON orden → Usar SolicitarFacturaModal
                    setShowRegenerateModal(true);
                  } else {
                    // Factura SIN orden → Usar CrearFacturaModal
                    setShowRegenerateWithoutOrderModal(true);
                  }
                }}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
                disabled={Boolean(factura.orderId && !order)}
                title={
                  factura.orderId
                    ? (!order ? 'Cargando orden...' : 'Regenerar factura con nuevos datos de orden')
                    : 'Regenerar factura con nuevos datos manuales'
                }
              >
                Regenerar Factura
              </Button>
            )}

            {puedeOperar && estadoVisible === 'CancelacionEnProceso' && (
              <Button
                icon={<SyncOutlined />}
                onClick={handleConsultarSat}
                loading={consultandoSat}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Consultar al SAT
              </Button>
            )}

            {(factura.esGlobal || amparaVarias) && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportarTickets}
                loading={cargandoTickets}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Exportar tickets (CSV)
              </Button>
            )}

            {puedeGestionar && (factura.ticketsPorSacar ?? 0) > 0 && (
              <Button
                icon={<ScissorOutlined />}
                onClick={handleReexpedirGlobal}
                loading={reexpidiendo}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Reexpedir global ahora
              </Button>
            )}

            {/* Motivo 04: sacar este ticket de la global para facturarlo al cliente */}
            {puedeGestionar && factura.esGlobal && estadoVisible === 'Timbrada' && contextOrderId && order && (
              <Button
                icon={<UserSwitchOutlined />}
                onClick={() => setShowFacturarTicketModal(true)}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Facturar a nombre del cliente
              </Button>
            )}

            {puedeGestionar && factura.uuid
              && (estadoVisible === 'Timbrada' || estadoVisible === 'CancelacionRechazada') && (
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleCancelar}
                loading={cancelandoFactura}
                size="large"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Cancelar Factura
              </Button>
            )}
          </Space>

          {/* Alertas informativas */}
          {!factura.uuid && factura.estado === 'Borrador' && (
            <Alert
              message="Factura en borrador"
              description="Esta factura aún no ha sido timbrada fiscalmente. Puede timbrarla ahora o más tarde."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {estadoVisible === 'CancelacionEnProceso' && (
            <Alert
              message="Cancelación en proceso"
              description="Se pidió la cancelación y el SAT aún no la confirma. Si el cliente debe aceptarla tiene hasta 3 días hábiles; mientras tanto la factura sigue vigente."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {estadoVisible === 'CancelacionRechazada' && (
            <Alert
              message="El cliente rechazó la cancelación"
              description="La factura sigue vigente ante el SAT."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {factura.estado === 'Cancelada' && (
            <Alert
              message="Factura cancelada"
              description="Esta factura ha sido cancelada ante el SAT y ya no es válida fiscalmente."
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </>
      )}

      {/* Modal de Regenerar Factura CON Orden */}
      {order && (
        <SolicitarFacturaModal
          visible={showRegenerateModal}
          onCancel={() => setShowRegenerateModal(false)}
          order={order}
          facturaIdToRegenerate={facturaId}
          onSuccess={(newFactura) => {
            setShowRegenerateModal(false);
            message.success('Factura regenerada exitosamente');
            // Si hay callback para notificar el nuevo ID, usarlo y cerrar este modal
            if (onFacturaRegenerated && newFactura.facturaId !== facturaId) {
              onClose();
              onFacturaRegenerated(newFactura.facturaId);
            } else {
              // Si es el mismo ID (no debería pasar) o no hay callback, refrescar
              refetch();
            }
          }}
        />
      )}

      {/* Motivo 04: factura del cliente para un ticket que está en la global */}
      {order && factura?.esGlobal && (
        <SolicitarFacturaModal
          visible={showFacturarTicketModal}
          onCancel={() => setShowFacturarTicketModal(false)}
          order={order}
          facturaGlobalId={facturaId}
          onSuccess={(nueva) => {
            setShowFacturarTicketModal(false);
            onRefresh?.();
            if (onFacturaRegenerated) {
              onClose();
              onFacturaRegenerated(nueva.facturaId);
            } else {
              refetch();
            }
          }}
        />
      )}

      {/* Modal de Regenerar Factura SIN Orden */}
      <CrearFacturaModal
        visible={showRegenerateWithoutOrderModal}
        onCancel={() => setShowRegenerateWithoutOrderModal(false)}
        mode="regenerate"
        facturaToRegenerate={factura}
        onSuccess={(newFactura) => {
          setShowRegenerateWithoutOrderModal(false);
          message.success('Factura regenerada exitosamente');
          if (onFacturaRegenerated && newFactura.facturaId !== facturaId) {
            onClose();
            onFacturaRegenerated(newFactura.facturaId);
          } else {
            refetch();
          }
        }}
      />

      {/* Modal de Enviar Email */}
      <Modal
        title="Enviar Factura por Email"
        open={showEmailModal}
        onCancel={() => {
          setShowEmailModal(false);
          emailForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '100%' : 500}
      >
        <Form
          form={emailForm}
          layout="vertical"
          onFinish={handleEnviarEmail}
        >
          <Form.Item
            name="emailDestino"
            label="Email de destino"
            rules={[
              { type: 'email', message: 'Por favor ingrese un email válido' },
            ]}
            extra={factura?.clienteEmail ? `Email del cliente: ${factura.clienteEmail}` : 'No hay email del cliente registrado'}
          >
            <Input
              placeholder="Ingrese el email (opcional, usa el del cliente si está vacío)"
              prefix={<MailOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="mensaje"
            label="Mensaje personalizado (opcional)"
          >
            <Input.TextArea
              rows={3}
              placeholder="Escriba un mensaje para incluir en el correo..."
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setShowEmailModal(false);
                emailForm.resetFields();
              }}>
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={enviandoEmail}
                icon={<MailOutlined />}
              >
                Enviar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
};

export default FacturaDetalleModal;
